import React from "react";
import { B2B } from "@/constants/testIds";
import { BRANDS } from "@/data/brands";
import { useT } from "@/i18n/LanguageContext";
import { Field, inputCls } from "./shared";

// All B2B form fields — text inputs, brand select, inquiry-type chip radios
// and the message textarea. Kept in a single component because they share
// the same layout grid and update handler; splitting further would just add
// prop-drilling with no maintainability win.
export default function B2BFormFields({ form, update }) {
  const { t } = useT();
  const inquiryTypes = [
    { value: "distribution", label: t("b2b.typeDistribution") },
    { value: "horeca", label: t("b2b.typeHoreca") },
    { value: "private-label", label: t("b2b.typePrivate") },
    { value: "retail", label: t("b2b.typeRetail") },
    { value: "other", label: t("b2b.typeOther") },
  ];

  return (
    <>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label={t("b2b.lblFullName")}>
          <input
            data-testid={B2B.fullName}
            required
            value={form.full_name}
            onChange={update("full_name")}
            placeholder={t("b2b.phFullName")}
            className={inputCls}
          />
        </Field>
        <Field label={t("b2b.lblCompany")}>
          <input
            data-testid={B2B.company}
            required
            value={form.company}
            onChange={update("company")}
            placeholder={t("b2b.phCompany")}
            className={inputCls}
          />
        </Field>
        <Field label={t("b2b.lblEmail")}>
          <input
            data-testid={B2B.email}
            required
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder={t("b2b.phEmail")}
            className={inputCls}
          />
        </Field>
        <Field label={t("b2b.lblPhone")}>
          <input
            data-testid={B2B.phone}
            value={form.phone}
            onChange={update("phone")}
            placeholder={t("b2b.phPhone")}
            className={inputCls}
          />
        </Field>
        <Field label={t("b2b.lblCountry")}>
          <input
            data-testid={B2B.country}
            required
            value={form.country}
            onChange={update("country")}
            placeholder={t("b2b.phCountry")}
            className={inputCls}
          />
        </Field>
        <Field label={t("b2b.lblBrand")}>
          <select
            data-testid={B2B.brand}
            value={form.brand_of_interest}
            onChange={update("brand_of_interest")}
            className={inputCls}
          >
            <option value="">{t("b2b.phBrandAny")}</option>
            {BRANDS.map((b) => (
              <option key={b.slug} value={b.name}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-5">
        <Field label={t("b2b.lblInquiryType")}>
          <div className="flex flex-wrap gap-2">
            {inquiryTypes.map((it) => {
              const selected = form.inquiry_type === it.value;
              return (
                <label
                  key={it.value}
                  className={`cursor-pointer px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                    selected
                      ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                      : "bg-white text-neutral-800 border-black/15 hover:border-black/50"
                  }`}
                >
                  <input
                    data-testid={selected ? B2B.inquiryType : undefined}
                    type="radio"
                    name="inquiry_type"
                    value={it.value}
                    checked={selected}
                    onChange={update("inquiry_type")}
                    className="sr-only"
                  />
                  {it.label}
                </label>
              );
            })}
          </div>
        </Field>
      </div>

      <div className="mt-5">
        <Field label={t("b2b.lblMessage")}>
          <textarea
            data-testid={B2B.message}
            required
            minLength={10}
            value={form.message}
            onChange={update("message")}
            rows={5}
            placeholder={t("b2b.phMessage")}
            className={`${inputCls} resize-none`}
          />
        </Field>
      </div>
    </>
  );
}
